import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Phone, X } from "lucide-react";
import { useLang } from "@/hooks/useLang";
import { UI, BUSINESS, SERVICES, serviceUrl, serviceTitle, trackEvent } from "@/content/site";
import "./header.css";
import ROUTE_PAIRS from "@/content/route-pairs";

/**
 * Resolve the href for the opposite-language version of the current page.
 * Prefers the page's own hreflang alternate, falls back to the language home.
 * The switcher must do a full document load so the server returns that
 * language's rendered HTML (a client-side route change only swaps the chrome).
 */
function otherLangHref(otherLang, fallback) {
  if (typeof window === "undefined") return fallback;
  // 1. The generated EN<->ES route map: same page, other language.
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const paired = ROUTE_PAIRS[path] || ROUTE_PAIRS[path + "/"];
  if (paired) return paired;
  // 2. Fall back to the page's own hreflang alternate (blog posts etc.).
  try {
    const el = document.querySelector(
      'link[rel="alternate"][hreflang="' + otherLang + '"]'
    );
    const href = el && el.getAttribute("href");
    if (href) return new URL(href, window.location.origin).pathname;
  } catch (_) {}
  // 3. Last resort: the other language's homepage.
  return fallback;
}

const SCROLL_THRESHOLD = 40;

const navLinkClass = ({ isActive }) => (isActive ? "gw-nav-link active" : "gw-nav-link");

/** Desktop mega menu for Family Law dropdown. */
function FamilyLawMegaMenu({ lang, ui }) {
  const [open, setOpen] = useState(false);
  const handleEnter = useCallback(() => setOpen(true), []);
  const handleLeave = useCallback(() => setOpen(false), []);
  const handleToggle = useCallback(() => setOpen((v) => !v), []);
  const handleKeyDown = useCallback((e) => {
    // WCAG 2.1.1 Keyboard — close the menu on Escape and shift focus back
    // to the trigger button so users don't get trapped.
    if (e.key === "Escape" && open) {
      setOpen(false);
      e.currentTarget.focus();
    }
  }, [open]);

  return (
    <div
      className="gw-nav-item gw-nav-dropdown"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className={`gw-nav-link ${open ? "active" : ""}`}
        aria-expanded={open}
        aria-controls="gw-family-menu"
        aria-haspopup="menu"
        data-testid="nav-family-law-trigger"
        onClick={handleToggle}
      >
        {ui.navFamilyLaw} <ChevronDown size={14} strokeWidth={2.4} aria-hidden="true" style={{marginLeft: 4}} />
      </button>
      {open && (
        <div
          className="gw-mega-menu"
          id="gw-family-menu"
          role="menu"
          aria-label={lang === "en" ? "Family law practice areas" : "Áreas de derecho familiar"}
        >
          <div className="gw-mega-menu-cols">
            {SERVICES.map((s) => (
              <Link key={s.key} to={serviceUrl(s, lang)} className="gw-mega-item" role="menuitem">
                <span className="gw-mega-title">{serviceTitle(s, lang)}</span>
              </Link>
            ))}
          </div>
          <div className="gw-mega-footer">
            <Link to={ui.familyLawUrl}>
              {lang === "en" ? "See all family law practice areas →" : "Ver todas las áreas de derecho familiar →"}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/** Desktop primary navigation. */
function DesktopNav({ lang, ui, contactUrl }) {
  return (
    <nav className="gw-nav-desktop" aria-label="Primary">
      <NavLink to={lang === "en" ? "/" : "/es"} end className={navLinkClass}>
        {ui.navHome}
      </NavLink>
      <FamilyLawMegaMenu lang={lang} ui={ui} />
      <NavLink to={ui.businessLawUrl} className={navLinkClass}>{ui.navBusinessLaw}</NavLink>
      <NavLink to={ui.aboutUrl} className={navLinkClass}>{ui.navAbout}</NavLink>
      <NavLink to={ui.blogUrl} className={navLinkClass}>{ui.navBlog}</NavLink>
      <NavLink to={contactUrl} className={navLinkClass}>{ui.navContact}</NavLink>
    </nav>
  );
}

/** Desktop header CTAs (phone, consult, language). */
function HeaderCta({ lang, ui, contactUrl, otherLang }) {
  const handlePhoneClick = useCallback(() => {
    trackEvent("phone_click", { location: "header", page: window.location.pathname });
  }, []);
  const handleConsultClick = useCallback(() => {
    trackEvent("cta_click", { location: "header", label: "consultation" });
  }, []);
  const handleLangClick = useCallback(() => {
    trackEvent("es_toggle_click", { from: lang, to: otherLang });
  }, [lang, otherLang]);

  return (
    <div className="gw-header-cta">
      <a
        href={`tel:${BUSINESS.phoneTel}`}
        className="gw-phone-link"
        onClick={handlePhoneClick}
      >
        <Phone size={16} strokeWidth={2.2} aria-hidden="true" />
        <span className="gw-phone-num">{BUSINESS.phoneDisplay}</span>
      </a>
      <Link
        to={contactUrl}
        className="gw-btn gw-btn-accent gw-header-consult"
        onClick={handleConsultClick}
      >
        {ui.ctaConsultation}
      </Link>
      <a href={ui.otherLangUrl}
        className="gw-lang-toggle"
        aria-label={otherLang === "es" ? "Cambiar a español" : "Switch to English"}
        lang={otherLang}
        data-testid="header-lang-toggle"
            onClick={(e) => { e.preventDefault(); handleLangClick(); window.location.assign(otherLangHref(otherLang, ui.otherLangUrl)); }}
          >
        <span className={lang === "en" ? "active" : ""}>EN</span>
        <span aria-hidden="true">|</span>
        <span className={lang === "es" ? "active" : ""}>ES</span>
      </a>
    </div>
  );
}

/** Mobile menu overlay + drawer content.
 *
 * Renders through a React portal into ``document.body`` because the parent
 * ``<header>`` uses ``backdrop-filter`` which per CSS spec creates a
 * containing block for ``position: fixed`` descendants. Without the portal
 * the menu was constrained to the header's 76px box and its contents were
 * clipped/hidden on mobile (the actual production bug users reported).
 *
 * The Family-Law sub-list is controlled by React state instead of the native
 * ``<details>`` element because iOS Safari has multiple long-standing bugs
 * around tap-to-toggle on ``<summary>`` and rendering of children inside
 * ``<details>``.
 */
function MobileMenu({ lang, ui, contactUrl, otherLang, onClose }) {
  const [servicesOpen, setServicesOpen] = useState(false);

  // Lock body scroll while the drawer is open so mobile users don't scroll
  // the background page underneath the overlay. iOS Safari ignores plain
  // ``overflow: hidden`` on body once momentum scrolling has started, so we
  // pin the body with ``position: fixed`` and restore the exact scrollY on
  // close. This is the pattern from libraries like ``body-scroll-lock``.
  useEffect(() => {
    const scrollY = window.scrollY;
    const previous = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = previous.overflow;
      document.body.style.position = previous.position;
      document.body.style.top = previous.top;
      document.body.style.width = previous.width;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Dismiss on Escape for keyboard users; treated as accessibility polish
  // more than a mobile-tap concern but costs nothing.
  useEffect(() => {
    const handler = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // "Click outside the drawer to dismiss" — this catches taps on the visible
  // backdrop area AND anywhere in the DOM that isn't part of the drawer.
  // More reliable than relying on the fixed backdrop element alone, because
  // it works no matter how wide/narrow the drawer is on the current screen.
  useEffect(() => {
    const handler = (event) => {
      const drawer = document.querySelector('[data-testid="mobile-menu"]');
      const toggle = document.querySelector('[data-testid="mobile-menu-toggle"]');
      if (!drawer || drawer.contains(event.target)) return;
      if (toggle && toggle.contains(event.target)) return;
      onClose();
    };
    // Use pointerdown so we catch iOS taps before the drawer's own click
    // handlers get a chance to swallow them.
    document.addEventListener("pointerdown", handler, true);
    return () => document.removeEventListener("pointerdown", handler, true);
  }, [onClose]);

  const toggleServices = useCallback(() => setServicesOpen((v) => !v), []);
  const handlePhoneClick = useCallback(() => {
    trackEvent("phone_click", { location: "mobile-menu" });
  }, []);

  const menu = (
    <>
      <button
        type="button"
        className="gw-mobile-backdrop"
        aria-label={lang === "en" ? "Close menu" : "Cerrar menú"}
        onClick={onClose}
        data-testid="mobile-menu-backdrop"
      />
      <div
        className="gw-mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label={lang === "en" ? "Mobile navigation" : "Navegación móvil"}
        data-testid="mobile-menu"
      >
        <div className="gw-mobile-menu-header">
          <span className="gw-mobile-menu-title">
            {lang === "en" ? "Menu" : "Menú"}
          </span>
          <button
            type="button"
            className="gw-mobile-close"
            aria-label={lang === "en" ? "Close menu" : "Cerrar menú"}
            onClick={onClose}
            data-testid="mobile-menu-close"
          >
            <X size={22} strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
        <nav aria-label="Mobile primary">
          <NavLink
            to={lang === "en" ? "/" : "/es"}
            end
            className="gw-mobile-link"
            onClick={onClose}
            data-testid="mobile-nav-home"
          >
            {ui.navHome}
          </NavLink>

          <div className="gw-mobile-services">
            <button
              type="button"
              className="gw-mobile-link gw-mobile-services-toggle"
              aria-expanded={servicesOpen}
              aria-controls="mobile-services-panel"
              onClick={toggleServices}
              data-testid="mobile-services-toggle"
            >
              <span>{ui.navFamilyLaw}</span>
              <ChevronDown
                size={20}
                strokeWidth={2.2}
                aria-hidden="true"
                className={`gw-mobile-services-caret${servicesOpen ? " open" : ""}`}
              />
            </button>
            {servicesOpen && (
              <ul
                id="mobile-services-panel"
                className="gw-mobile-services-panel"
                data-testid="mobile-services-panel"
              >
                <li>
                  <NavLink
                    to={ui.familyLawUrl}
                    className="gw-mobile-sublink gw-mobile-sublink-overview"
                    onClick={onClose}
                    data-testid="mobile-family-law-overview"
                  >
                    {lang === "en"
                      ? "All Family Law Areas"
                      : "Todas las áreas familiares"}
                  </NavLink>
                </li>
                {SERVICES.map((s) => (
                  <li key={s.key}>
                    <Link
                      to={serviceUrl(s, lang)}
                      className="gw-mobile-sublink"
                      onClick={onClose}
                      data-testid={`mobile-service-${s.key}`}
                    >
                      {serviceTitle(s, lang)}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <NavLink
            to={ui.businessLawUrl}
            className="gw-mobile-link"
            onClick={onClose}
            data-testid="mobile-nav-business-law"
          >
            {ui.navBusinessLaw}
          </NavLink>
          <NavLink
            to={ui.aboutUrl}
            className="gw-mobile-link"
            onClick={onClose}
            data-testid="mobile-nav-about"
          >
            {ui.navAbout}
          </NavLink>
          <NavLink
            to={ui.blogUrl}
            className="gw-mobile-link"
            onClick={onClose}
            data-testid="mobile-nav-blog"
          >
            {ui.navBlog}
          </NavLink>
          <NavLink
            to={contactUrl}
            className="gw-mobile-link"
            onClick={onClose}
            data-testid="mobile-nav-contact"
          >
            {ui.navContact}
          </NavLink>

          <div className="gw-mobile-cta">
            <a
              href={`tel:${BUSINESS.phoneTel}`}
              className="gw-btn gw-btn-primary"
              onClick={handlePhoneClick}
              data-testid="mobile-cta-call"
            >
              {ui.ctaCall}
            </a>
            <Link
              to={contactUrl}
              className="gw-btn gw-btn-accent"
              onClick={onClose}
              data-testid="mobile-cta-consult"
            >
              {ui.ctaConsultation}
            </Link>
            <a href={ui.otherLangUrl}
              className="gw-btn gw-btn-ghost"
              data-testid="mobile-cta-lang"
            onClick={(e) => { e.preventDefault(); onClose(); window.location.assign(otherLangHref(otherLang, ui.otherLangUrl)); }}
          >
              {ui.languageToggle}
            </a>
          </div>
        </nav>
      </div>
    </>
  );

  // SSR safety: `document` isn't defined during server-side prerender, so we
  // gate the portal on window existence.
  if (typeof document === "undefined") return menu;
  return createPortal(menu, document.body);
}

/** Custom hook: track scroll state past a threshold. */
function useScrolled(threshold) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

export default function Header() {
  const lang = useLang();
  const ui = UI[lang];
  const scrolled = useScrolled(SCROLL_THRESHOLD);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on language change.
  useEffect(() => {
    setMobileOpen(false);
  }, [lang]);

  const contactUrl = ui.contactUrl;
  const otherLang = lang === "en" ? "es" : "en";
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);

  return (
    <header
      className={`gw-header ${scrolled ? "gw-header-scrolled" : ""}`}
      role="banner"
    >
      <div className="gw-container gw-header-inner">
        <Link
          to={lang === "en" ? "/" : "/es"}
          className="gw-logo"
          aria-label="Garcia-Windsor, P.C. home"
          data-testid="site-logo-link"
        >
          <img
            src="/images/logo.png"
            alt="Garcia-Windsor Attorneys · Abogados"
            className="gw-logo-image"
            width="493"
            height="174"
            decoding="async"
            fetchpriority="high"
            data-testid="site-logo-image"
          />
        </Link>

        <DesktopNav lang={lang} ui={ui} contactUrl={contactUrl} />
        <HeaderCta lang={lang} ui={ui} contactUrl={contactUrl} otherLang={otherLang} />

        <button
          type="button"
          className={`gw-mobile-toggle${mobileOpen ? " open" : ""}`}
          aria-label={
            mobileOpen
              ? (lang === "es" ? "Cerrar menú" : "Close menu")
              : (lang === "es" ? "Abrir menú" : "Open menu")
          }
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-haspopup="menu"
          onClick={toggleMobile}
          data-testid="mobile-menu-toggle"
        >
          <span aria-hidden="true"></span><span aria-hidden="true"></span><span aria-hidden="true"></span>
        </button>
      </div>

      {mobileOpen && (
        <MobileMenu
          lang={lang}
          ui={ui}
          contactUrl={contactUrl}
          otherLang={otherLang}
          onClose={closeMobile}
        />
      )}
    </header>
  );
}
